import { useApolloClient } from "@apollo/client";
import { type SessionV2, types as dqCoreTypes } from "@decentriq/core";
import {
  type DataConnectorJobFragment,
  DataConnectorJobSetToFailureDocument,
  DataConnectorJobSetToSuccessDocument,
} from "@decentriq/graphql/dist/types";
import { useCallback, useMemo } from "react";
import { useApiCore } from "contexts";
import { delay, logWarning } from "utils";

interface ComputeV2DatasetImportWatcherHookResult {
  poll: (connectorJob: DataConnectorJobFragment) => Promise<void>;
}

const useComputeV2DatasetImportWatcher =
  (): ComputeV2DatasetImportWatcherHookResult => {
    const apolloClient = useApolloClient();
    const { getSession, client } = useApiCore();
    const downloadJobResult = useCallback(
      async (
        jobId: string,
        resultHash: string,
        session: SessionV2
      ): Promise<Uint8Array> => {
        const [manifestHashHex, encryptionKey] =
          await session.sendRetrieveResultEncryptionKeyRequest(
            jobId,
            resultHash
          );
        const stream = await client.downloadJobResult(
          jobId,
          manifestHashHex,
          resultHash,
          encryptionKey
        );
        return await client.readStream(stream);
      },
      [client]
    );
    const poll = useCallback(
      async (connectorJob: DataConnectorJobFragment) => {
        try {
          const { jobId, zipPath } = JSON.parse(connectorJob.config!) as {
            jobId: string;
            zipPath: string;
          };
          let jobStatus: dqCoreTypes.JobStatusView;
          while (true) {
            jobStatus = await client.getJobStatus(jobId);
            if (jobStatus.completed_at !== null) {
              break;
            }
            await delay(3000);
          }
          const jobTasks = await client.getJobTasks(jobId);
          const session = await getSession();
          if (jobStatus.status === dqCoreTypes.JobStatus.FAILED) {
            const errorResults = [];
            for (const task of Object.values(jobTasks.tasks)) {
              for (const [selector, resultHash] of Object.entries(
                task.results
              )) {
                if (selector.startsWith("error")) {
                  const error = await downloadJobResult(
                    jobId,
                    resultHash,
                    session
                  );
                  errorResults.push([
                    task.name,
                    new TextDecoder().decode(error),
                  ]);
                }
              }
            }
            throw new Error(
              `Job failed with errors: ${errorResults.map(([name, error]) => `${name}: ${error}`).join("\n")}`
            );
          }
          const jobTaskResultHash =
            jobTasks.tasks[jobTasks.target_task].results["success"];
          if (jobTaskResultHash === null) {
            throw new Error("Did not find job task result");
          }
          await session.sendExportResultAsDatasetRequest(
            jobId,
            jobTaskResultHash,
            connectorJob.datasetName ?? "",
            zipPath
          );
          await apolloClient.mutate({
            mutation: DataConnectorJobSetToSuccessDocument,
            variables: {
              id: connectorJob.id,
            },
          });
        } catch (error) {
          logWarning(error, "Error while polling compute v2 dataset import");
          await apolloClient.mutate({
            mutation: DataConnectorJobSetToFailureDocument,
            variables: {
              id: connectorJob.id,
            },
          });
        }
      },
      [apolloClient, client, downloadJobResult, getSession]
    );
    return useMemo(
      () => ({
        poll,
      }),
      [poll]
    );
  };

export default useComputeV2DatasetImportWatcher;
